import { Component, Signal, computed, effect, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  catchError,
  combineLatest,
  filter,
  forkJoin,
  map,
  of,
  startWith,
  switchMap
} from 'rxjs';

import { ICONS } from '@gfe/core/services/icons/icons.service';
import { isNil } from '@gfe/core/utils/shared-utils';
import {
  SearchConfig,
  ToolboxComponent,
  ToolboxSection
} from '@gfe/shared/components/toolbox/toolbox.component';

import {
  SolverParameterListDto,
  SolverParameterSummaryDto
} from '@core/models/parameter.model';
import { ParamsService } from '@core/services/params/params.service';
import {
  FetchAllSolversResponse,
  SolverService
} from '@core/services/solvers/solvers.service';
import { selectDataspaceModel } from '@core/store/dataspace/dataspace.selectors';
import { foldForSearch } from '@core/utils/search-text.utils';

import {
  ModalComponent,
  ModalConfig,
  ModalType
} from '@shared/components/modal/modal.component';
import { TableComponent } from '@shared/components/table/table.component';
import {
  TableAction,
  TableColumn,
  TableConfig
} from '@shared/components/table/table.model';

import { NewParams } from '@features/components/solver/new-params/new-params';
import { UpdateParams } from '@features/components/solver/update-params/update-params';

@Component({
  selector: 'app-solver',
  imports: [ToolboxComponent, NewParams, UpdateParams, TableComponent, ModalComponent],
  templateUrl: './solver.component.html',
  styles: ``
})
export class SolverComponent {
  private readonly store = inject(Store);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly translate = inject(TranslateService);
  private readonly solversService = inject(SolverService);
  private readonly paramsService = inject(ParamsService);

  protected displayedData = signal(new Set<SolverParameterSummaryDto>());
  protected newSolverConfigModalIsOpen = signal<boolean>(false);
  protected editSolverConfigModalIsOpen = signal<boolean>(false);
  protected reloadPage = signal<boolean>(false);
  protected search = signal<string>('');
  solverType = signal<number>(0);

  protected searchPlaceholder = computed(() => {
    this.currentLang(); // Trigger reactivity when language changes
    return this.translate.instant('common.search_placeholder');
  });

  protected selectedParamConfig = signal<{
    id: number;
    name: string;
    description: string;
    solverId: number;
  }>({
    id: 0,
    name: '',
    description: '',
    solverId: 0
  });

  protected newSolverConfigModalConfig: ModalConfig = {
    dismissible: true,
    closeOnBackdropClick: true
  };

  protected readonly newSolverTitleKey = 'solver.add';
  protected newSolverTitle = computed(() => {
    this.currentLang();
    return this.translate.instant(this.newSolverTitleKey);
  });

  protected readonly updateSolverTitleKey = 'solver.update';
  protected updateSolverTitle = computed(() => {
    this.currentLang();
    return this.translate.instant(this.updateSolverTitleKey);
  });

  protected deleteMetadataModal = signal<{
    isOpen: boolean;
    itemIds: string[];
    config: ModalConfig;
    title: Signal<string>;
    message: Signal<string>;
  }>({
    isOpen: false,
    itemIds: [],
    config: {
      icon: 'warning',
      type: ModalType.ERROR,
      dismissible: true,
      closeOnBackdropClick: true,
      askingModal: true,
      actions: [
        {
          label: this.translate.instant('common.cancel'),
          callback: () => {
            this.deleteMetadataModal.set({
              ...this.deleteMetadataModal(),
              isOpen: false
            });
          },
          color: 'cancel'
        },
        {
          label: this.translate.instant('common.submit'),
          callback: () => {
            this.onDeleteMetadatas(this.deleteMetadataModal().itemIds);
          },
          color: 'danger',
          icon: 'trash'
        }
      ]
    },
    title: computed(() => {
      return this.translate.instant('features.solver.modal.title');
    }),
    message: computed(() => {
      return this.translate.instant('features.solver.modal.description');
    })
  });

  constructor() {
    effect(() => {
      const routeSegment = this.activatedRoute.snapshot?.params?.['solver'];
      if (routeSegment) {
        this.solverType.set(Number(routeSegment));
      }
    });
  }

  protected currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang)
    ),
    { initialValue: this.translate.getCurrentLang() }
  );

  hasSelectionSignal = computed(() => this.displayedData().size > 0);

  solverConfigs = toSignal(
    combineLatest([
      this.store.select(selectDataspaceModel).pipe(filter((x) => !isNil(x))),
      toObservable(this.reloadPage).pipe(startWith(false))
    ]).pipe(
      switchMap(([dataspace]) =>
        this.solversService.getSolverList$(dataspace.id).pipe(
          map((solvers: FetchAllSolversResponse[]) => {
            return solvers.find((solver) => solver.id === this.solverType()) || null;
          }),
          catchError((error) => {
            console.error('Error fetching solver configs:', error);
            return of(null);
          })
        )
      )
    ),
    { initialValue: null }
  );

  paramsConfigs = toSignal(
    combineLatest([
      toObservable(this.search),
      toObservable(this.reloadPage).pipe(startWith(false)),
      toObservable(this.solverConfigs).pipe(filter((s) => !!s))
    ]).pipe(
      switchMap(([search]) => {
        const solverId = this.solverConfigs()?.id;
        if (!solverId || solverId === 0) {
          return of([] as SolverParameterSummaryDto[]);
        }

        const params = this.paramsService.getParamsBySolverId$(solverId).pipe(
          map((response: SolverParameterListDto) => {
            console.log('Fetched params:', response);
            return response?.result || [];
          }),
          catchError((error) => {
            console.error('Error fetching params:', error);
            return of([] as SolverParameterSummaryDto[]);
          })
        );
        return params.pipe(
          map((paramsList) => {
            if (search && search.trim().length > 0) {
              return paramsList.filter((param) =>
                foldForSearch(param.name).includes(foldForSearch(search))
              );
            } else {
              return paramsList;
            }
          })
        );
      })
    ),
    { initialValue: [] as SolverParameterSummaryDto[] }
  );

  columns = computed<TableColumn<SolverParameterSummaryDto>[]>(() => {
    this.currentLang();
    return [
      {
        key: 'name',
        label: this.translate.instant('common.name'),
        sortable: true,
        type: 'string',
        width: 80,
        cellTemplate: 'default'
      },
      {
        key: 'createdAt',
        label: this.translate.instant('common.created'),
        sortable: true,
        type: 'string',
        width: 100,
        cellTemplate: 'default',
        renderHook(value) {
          return new Date(value as string).toLocaleString();
        }
      },
      {
        key: 'description',
        label: this.translate.instant('common.description'),
        sortable: true,
        type: 'string',
        width: 250,
        cellTemplate: 'default'
      }
    ];
  });

  actions = signal<TableAction<SolverParameterSummaryDto>[]>([
    // cannot edit parameters for now
    // {
    //   label: 'edit',
    //   icon: ICONS.gear,
    //   color: 'neutral',
    //   action: (row: SolverParameterSummaryDto): void => {
    //     this.router.navigate(['config', row.id.toString()], {
    //       relativeTo: this.activatedRoute
    //     });
    //   }
    // }
  ]);

  get tableConfig(): TableConfig<SolverParameterSummaryDto> {
    return {
      selection: {
        enabled: true,
        multiple: true,
        mode: 'checkbox',
        action: (row: SolverParameterSummaryDto) => {
          const currentSelected = new Set(this.displayedData());
          if (currentSelected.has(row)) {
            currentSelected.delete(row);
          } else {
            currentSelected.add(row);
          }
          this.displayedData.set(currentSelected);
        }
      }
    };
  }

  toolboxSections = computed<ToolboxSection[]>(() => {
    this.currentLang();
    const selectedItems = Array.from(this.displayedData());
    const selection = this.hasSelectionSignal();
    return [
      {
        id: 'default actions',
        items: [
          {
            id: 'add',
            icon: ICONS.plus,
            tooltip: this.translate.instant('solver.add'),
            callback: () => {
              this.newSolverConfigModalIsOpen.set(true);
            }
          },
          {
            id: 'edit',
            icon: ICONS.pencil,
            tooltip: this.translate.instant('common.edit'),
            disabled: !selection || selectedItems.length !== 1,
            callback: () => {
              if (selectedItems.length === 1) {
                this.selectedParamConfig.set({
                  id: Number(selectedItems[0].id),
                  name: selectedItems[0].name,
                  description: selectedItems[0].description ?? '',
                  solverId: isNaN(Number(this.solverConfigs()?.id))
                    ? 0
                    : Number(this.solverConfigs()?.id)
                });
                this.editSolverConfigModalIsOpen.set(true);
                this.displayedData.set(new Set<SolverParameterSummaryDto>());
              } else {
                console.warn('Please select exactly one solver to edit.');
              }
            }
          }
        ]
      },
      // Actions available when table items are selected
      {
        id: 'bulk-actions',
        items: [
          {
            id: 'delete',
            icon: ICONS.trash,
            tooltip: this.translate.instant('common.delete'),
            disabled: !selection,
            callback: () => {
              this.deleteMetadataModal.set({
                ...this.deleteMetadataModal(),
                isOpen: true,
                itemIds: Array.from(this.displayedData()).map((item) => item.id.toString())
              });
            }
          }
        ]
      }
    ];
  });

  searchCfg: SearchConfig = {
    active: true,
    type: 'simple',
    shortcut: true
  };

  onDeleteMetadatas(event: string[]): void {
    console.log('Deleting solver configs with IDs:', event);
    if ((event ?? []).length > 0) {
      const deleteObservables = event.map((value) => {
        const id = Number(value);
        return this.paramsService.deleteParam$(id, Number(this.solverConfigs()?.id) || 0);
      });

      forkJoin(deleteObservables).subscribe({
        next: () => {
          console.log('All solver configs deleted successfully');
          this.reloadPage.set(!this.reloadPage());
        },
        error: (err) => {
          console.error('Error deleting solver configs:', err);
          this.reloadPage.set(!this.reloadPage());
        },
        complete: () => {
          this.deleteMetadataModal.set({
            ...this.deleteMetadataModal(),
            isOpen: false,
            itemIds: []
          });
          this.onDeselectAll();
        }
      });
    }
  }

  submitNewSolver(solver: { name: string; description: string }): void {
    this.newSolverConfigModalIsOpen.set(false);

    const solverConfig = this.solverConfigs();
    if (!solverConfig?.id) {
      console.error('No solver config available');
      return;
    }

    const solverSubmitted = {
      name: solver.name || 'Untitled Solver',
      description: solver.description || '',
      solverId: solverConfig.id
    };

    this.paramsService.addParams$(solverSubmitted).subscribe({
      next: () => {
        console.log('New solver config created');
        this.reloadPage.set(!this.reloadPage());
      },
      error: (error) => {
        console.error('Error creating new solver config:', error);
      }
    });
  }

  submitUpdateParam(param: { name: string; description: string; id: number }): void {
    this.editSolverConfigModalIsOpen.set(false);

    const solverConfig = this.solverConfigs();
    if (!solverConfig?.id) {
      console.error('No solver config available');
      return;
    }

    const solverSubmitted = {
      id: param.id,
      name: param.name || 'Untitled Solver',
      description: param.description || ''
    };

    this.paramsService
      .updateParams$(param.id, Number(solverConfig.id), solverSubmitted)
      .subscribe({
        next: () => {
          console.log('Solver config updated');
          this.reloadPage.set(!this.reloadPage());
        },
        error: (error) => {
          console.error('Error updating solver config:', error);
        }
      });
  }

  onDeselectAll(): void {
    this.displayedData.set(new Set<SolverParameterSummaryDto>());
  }
}
